import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import axios from 'axios';

// Components — carregados imediatamente (rota principal do usuário)
import Navbar from './components/Navbar';
// LandingPage mantida no repo para uso futuro (página de vendas será refinada depois).
// Hoje a rota / vai direto para o Login para reduzir cliques.
// eslint-disable-next-line no-unused-vars
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import QuizLesson from './components/QuizLesson';
import VideoLesson from './components/VideoLesson';
import Simulator from './components/Simulator';
import SimulatorsPage from './components/SimulatorsPage';
import HeartOfAction from './components/HeartOfAction';
import Profile from './components/Profile';
import CircleView from './components/CircleView';
import InitialAssessment from './components/InitialAssessment';
import PersonalizationSurvey from './components/PersonalizationSurvey';
import OnboardingFlow from './components/OnboardingFlow';
import ProgressDashboard from './components/ProgressDashboard';
import PDIPage from './components/PDIPage';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import LessonModules from './components/LessonModules';
import InstrumentSurvey from './components/InstrumentSurvey';
import EmotionDiary from './components/EmotionDiary';
import EmotionDiaryWizard from './components/EmotionDiaryWizard';
import EmotionDiaryHistory from './components/EmotionDiaryHistory';
import EmotionDiaryInsights from './components/EmotionDiaryInsights';
import SubscriptionPlans from './components/SubscriptionPlans';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import EmergencyEmpathy from './components/EmergencyEmpathy';
import Paywall from './components/Paywall';
import MagicLogin from './components/MagicLogin';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import MeusDados from './components/MeusDados';
// GestorDashboard (não o "Improved"): este consome os endpoints reais
// /hr/gestor/my-group/* e exporta CSV legível. O "Improved" apontava para
// endpoints inexistentes (/hr-reports/*) — relatórios não carregavam e o
// export saía como .json. Ver docs/Plano_Acao_MVP.md (frente 2).
import GestorDashboard from './components/GestorDashboard';
import FloatingChat from './components/FloatingChat';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';
import WellbeingSurvey from './components/WellbeingSurvey';
import EmpatiaScale from './components/EmpatiaScale';
import IQRSurveyModal from './components/IQRSurveyModal';
import HRLogin from './components/hr/HRLogin';
import HRDashboard from './components/hr/HRDashboard';
import HRGroupDetail from './components/hr/HRGroupDetail';
import { HRAuthProvider, useHRAuth } from './contexts/HRAuthContext';

// Componentes admin — carregados sob demanda (lazy) para não inflar o bundle principal
const AdminDashboard    = React.lazy(() => import('./components/admin/AdminDashboard'));
const IslandsManager    = React.lazy(() => import('./components/admin/IslandsManager'));
const LessonsManager    = React.lazy(() => import('./components/admin/LessonsManager'));
const ModulesManager    = React.lazy(() => import('./components/admin/ModulesManager'));
const QuestionsManager  = React.lazy(() => import('./components/admin/QuestionsManager'));
const ContentTreeManager= React.lazy(() => import('./components/admin/ContentTreeManager'));
const SimulatorsManager = React.lazy(() => import('./components/admin/SimulatorsManager'));
const CirclesManager    = React.lazy(() => import('./components/admin/CirclesManager'));
const ReportsPage       = React.lazy(() => import('./components/admin/ReportsPage'));
const UsersManager      = React.lazy(() => import('./components/admin/UsersManager'));
const CouponsManager    = React.lazy(() => import('./components/admin/CouponsManager'));
const SettingsManager   = React.lazy(() => import('./components/admin/SettingsManager'));
const AIPromptsManager  = React.lazy(() => import('./components/admin/AIPromptsManager'));
const ExerciseErrorsLog = React.lazy(() => import('./components/admin/ExerciseErrorsLog'));
const InstrumentsManager= React.lazy(() => import('./components/admin/InstrumentsManager'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: wraps lazy-loaded admin components with auth check + Suspense.
// Must be defined OUTSIDE AppContent to avoid remounting on each render.
// Usage: <Route path="/admin" element={<AdminRoute component={AdminDashboard} />} />
const AdminRoute = ({ component: Comp }) => {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user?.isAdmin) return <Navigate to="/dashboard" />;
  return (
    <React.Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600" />
      </div>
    }>
      <Comp />
    </React.Suspense>
  );
};

// Helper component for routes that require subscription - defined outside to prevent remounting
const SubscriptionRequired = ({ children }) => {
  const { user, isAuthenticated, hasSubscription } = useAuth();

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user) return null; // guard: isAuthenticated pode ser true antes de user hidratar

  // Onboarding enxuto (Simby-3): a única etapa obrigatória de entrada é o vídeo
  // de boas-vindas. O Perfil Relacional (assessment) deixou de ser obrigatório —
  // fica opcional na aba "Plano de Desenvolvimento".
  const needsOnboarding = !user.hasCompletedOnboarding && !user.isAdmin && !user.isHrManager;
  if (needsOnboarding) return <Navigate to="/onboarding" />;

  if (!hasSubscription && !user.isAdmin && !user.isHrManager) return <Navigate to="/paywall" />;
  return children;
};

function AppContent() {
  const { user, isAuthenticated, isLoading, hasSubscription, updateUser } = useAuth();
  const [showWellbeing, setShowWellbeing] = React.useState(false);
  const [showEmpathy, setShowEmpathy] = React.useState(false);
  const [empathyLevel, setEmpathyLevel] = React.useState('baseline');
  const [showIQR, setShowIQR] = React.useState(false);

  // Check if user is a Gestor (HR Manager) - they should go to gestor dashboard
  const isGestor = user?.isHrManager && !user?.isAdmin;

  // Survey check: run once after the user is fully authenticated with a subscription
  React.useEffect(() => {
    if (!isAuthenticated || !user || !hasSubscription || user.isAdmin || user.isHrManager || isLoading) return;
    if (!user.hasCompletedInitialAssessment) return; // skip until onboarding is done

    const checkSurveys = async () => {
      try {
        const [wellbeingRes, empathyRes, iqrRes] = await Promise.all([
          axios.get(`${API}/surveys/wellbeing/status`, { withCredentials: true }),
          axios.get(`${API}/surveys/empathy/status`, { withCredentials: true }),
          axios.get(`${API}/surveys/iqr/status`, { withCredentials: true }).catch(() => ({ data: { pending: false } })),
        ]);

        if (wellbeingRes.data.due) {
          setShowWellbeing(true);
        } else if (empathyRes.data.next_level) {
          setEmpathyLevel(empathyRes.data.next_level);
          setShowEmpathy(true);
        } else if (iqrRes.data.pending) {
          setShowIQR(true);
        }
      } catch {
        // silent — never block the user
      }
    };

    checkSurveys();
  }, [isAuthenticated, isLoading]); // eslint-disable-line

  const handleWellbeingClose = () => {
    setShowWellbeing(false);
    // After wellbeing, check if empathy is due
    axios.get(`${API}/surveys/empathy/status`, { withCredentials: true })
      .then((res) => {
        if (res.data.next_level) {
          setEmpathyLevel(res.data.next_level);
          setShowEmpathy(true);
        }
      })
      .catch(() => {});
  };

  const handleAssessmentComplete = async (payload) => {
    try {
      const response = await axios.post(
        `${API}/auth/complete-assessment`,
        payload,
        { withCredentials: true }
      );
      updateUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to save assessment:', error);
      throw error;
    }
  };

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {isAuthenticated && user && <Navbar user={user} />}
      <Routes>
        {/* Public Routes */}
        {/* Raiz vai direto para o Login (ou redireciona se já autenticado) — fluxo simplificado de acesso. */}
        <Route path="/" element={isAuthenticated ? <Navigate to={isGestor ? "/gestor" : "/dashboard"} /> : <Login />} />
        <Route path="/login" element={isAuthenticated ? <Navigate to={isGestor ? "/gestor" : "/dashboard"} /> : <Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/magic-login" element={<MagicLogin />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/my-data" element={isAuthenticated ? <MeusDados /> : <Navigate to="/login" />} />

        {/* Onboarding Route */}
        <Route
          path="/onboarding"
          element={isAuthenticated ? <OnboardingFlow /> : <Navigate to="/login" />}
        />

        {/* Initial Assessment Route */}
        <Route
          path="/assessment"
          element={isAuthenticated ? <InitialAssessment onComplete={handleAssessmentComplete} /> : <Navigate to="/login" />}
        />

        {/* Personalização — perguntas opcionais, acessíveis pela aba Plano */}
        <Route
          path="/personalizacao"
          element={isAuthenticated ? <PersonalizationSurvey /> : <Navigate to="/login" />}
        />

        {/* Progress Dashboard */}
        <Route
          path="/progress"
          element={isAuthenticated ? <ProgressDashboard /> : <Navigate to="/login" />}
        />

        {/* PDI — Plano de Desenvolvimento Individual (briefing seção 8.1) */}
        <Route
          path="/pdi"
          element={<SubscriptionRequired><PDIPage /></SubscriptionRequired>}
        />

        {/* Paywall Route */}
        <Route
          path="/paywall"
          element={isAuthenticated ? <Paywall /> : <Navigate to="/login" />}
        />

        {/* Subscription Routes */}
        <Route
          path="/subscription/plans"
          element={isAuthenticated ? <SubscriptionPlans /> : <Navigate to="/login" />}
        />
        <Route
          path="/subscription/success"
          element={isAuthenticated ? <SubscriptionSuccess /> : <Navigate to="/login" />}
        />

        {/* Protected Routes - Require Subscription */}
        <Route
          path="/dashboard"
          element={<SubscriptionRequired><Dashboard /></SubscriptionRequired>}
        />
        <Route
          path="/lesson-modules/:lessonId"
          element={<SubscriptionRequired><LessonModules /></SubscriptionRequired>}
        />
        <Route
          path="/quiz/:lessonId"
          element={<SubscriptionRequired><QuizLesson /></SubscriptionRequired>}
        />
        <Route
          path="/lesson/:lessonId"
          element={<SubscriptionRequired><VideoLesson /></SubscriptionRequired>}
        />
        <Route
          path="/simulator/:lessonId"
          element={<SubscriptionRequired><Simulator /></SubscriptionRequired>}
        />
        <Route
          path="/simulators"
          element={<SubscriptionRequired><SimulatorsPage /></SubscriptionRequired>}
        />
        <Route
          path="/heart-of-action"
          element={<SubscriptionRequired><HeartOfAction /></SubscriptionRequired>}
        />
        <Route
          path="/circle"
          element={<SubscriptionRequired><CircleView /></SubscriptionRequired>}
        />
        <Route
          path="/profile"
          element={<SubscriptionRequired><Profile /></SubscriptionRequired>}
        />
        {/* Legacy redirects */}
        <Route path="/shop" element={<Navigate to="/circle" replace />} />
        <Route path="/leaderboard" element={<Navigate to="/circle" replace />} />
        <Route
          path="/emergency-empathy"
          element={<SubscriptionRequired><EmergencyEmpathy /></SubscriptionRequired>}
        />

        {/* Admin Routes — lazy-loaded via AdminRoute (auth + Suspense integrados) */}
        <Route path="/admin"                  element={<AdminRoute component={AdminDashboard}    />} />
        <Route path="/admin/islands"          element={<AdminRoute component={IslandsManager}    />} />
        <Route path="/admin/lessons"          element={<AdminRoute component={LessonsManager}    />} />
        <Route path="/admin/modules"          element={<AdminRoute component={ModulesManager}    />} />
        <Route path="/admin/questions"        element={<AdminRoute component={QuestionsManager}  />} />
        <Route path="/admin/content"          element={<AdminRoute component={ContentTreeManager}/>} />
        <Route path="/admin/simulators"       element={<AdminRoute component={SimulatorsManager} />} />
        <Route path="/admin/reports"          element={<AdminRoute component={ReportsPage}       />} />
        <Route path="/admin/users"            element={<AdminRoute component={UsersManager}      />} />
        <Route path="/admin/coupons"          element={<AdminRoute component={CouponsManager}    />} />
        <Route path="/admin/settings"         element={<AdminRoute component={SettingsManager}   />} />
        <Route path="/admin/ai-prompts"       element={<AdminRoute component={AIPromptsManager}  />} />
        <Route path="/admin/exercise-errors"  element={<AdminRoute component={ExerciseErrorsLog} />} />
        <Route path="/admin/circles"          element={<AdminRoute component={CirclesManager}    />} />
        <Route path="/admin/instruments"      element={<AdminRoute component={InstrumentsManager}/>} />

        {/* Instrument survey — available to all authenticated subscribers */}
        <Route
          path="/survey/:instrumentType"
          element={<SubscriptionRequired><InstrumentSurvey /></SubscriptionRequired>}
        />

        {/* Gestor Routes - for users with is_hr_manager=true */}
        <Route
          path="/gestor"
          element={
            isAuthenticated ? (
              user.isHrManager ? <GestorDashboard /> : <Navigate to="/dashboard" />
            ) : <Navigate to="/login" />
          }
        />

        {/* Diário de Emoções (briefing v2) — entrada do check-in fica no Dashboard.
            /diary → tela 'O que a Simby vem te dizendo'. Wizard TCC legacy fica em /diary/new. */}
        <Route
          path="/diary"
          element={<Navigate to="/diary/insights" replace />}
        />
        <Route
          path="/diary/v1"
          element={<SubscriptionRequired><EmotionDiary /></SubscriptionRequired>}
        />

        {/* Emotion Diary V2 - TCC-based */}
        <Route
          path="/diary/new"
          element={<SubscriptionRequired><EmotionDiaryWizard /></SubscriptionRequired>}
        />
        <Route
          path="/diary/history"
          element={<SubscriptionRequired><EmotionDiaryHistory /></SubscriptionRequired>}
        />
        <Route
          path="/diary/insights"
          element={<SubscriptionRequired><EmotionDiaryInsights /></SubscriptionRequired>}
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to={isAuthenticated ? (isGestor ? "/gestor" : "/dashboard") : "/login"} />} />
      </Routes>
      <Toaster />
      {isAuthenticated && <FloatingChat />}
      {isAuthenticated && <PWAInstallPrompt />}
      {showWellbeing && <WellbeingSurvey onClose={handleWellbeingClose} />}
      {showEmpathy && <EmpatiaScale level={empathyLevel} onClose={() => setShowEmpathy(false)} />}
      {showIQR && <IQRSurveyModal onClose={() => setShowIQR(false)} />}
    </div>
  );
}

// HR Portal Content Component
function HRContent() {
  const { manager, isAuthenticated, isLoading } = useHRAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/hr/login" element={isAuthenticated ? <Navigate to="/hr/dashboard" /> : <HRLogin />} />
      <Route
        path="/hr/dashboard"
        element={isAuthenticated ? <HRDashboard /> : <Navigate to="/hr/login" />}
      />
      <Route
        path="/hr/group/:groupName"
        element={isAuthenticated ? <HRGroupDetail /> : <Navigate to="/hr/login" />}
      />
      <Route path="/hr/*" element={<Navigate to="/hr/login" />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* HR Routes - wrapped in HRAuthProvider */}
            <Route path="/hr/*" element={
              <HRAuthProvider>
                <HRContent />
              </HRAuthProvider>
            } />
            {/* All other routes - handled by AppContent */}
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
